import { Box, Typography } from '@mui/material';
import { memo, useEffect, useState, useMemo } from 'react';
import quibbleaiLogoDark from '../../assets/images/quibbleaiLogoDark.svg';

// Floating background shapes
const FloatingShapes = memo(() => (
  <Box
    sx={{
      position: 'absolute',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      zIndex: 1,
    }}
  >
    {/* Shape 1 */}
    <Box
      sx={{
        position: 'absolute',
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        opacity: 0.03,
        top: '10%',
        left: '10%',
        animation: 'floatAround1 20s ease-in-out infinite',
        '@keyframes floatAround1': {
          '0%, 100%': { transform: 'translate(0, 0) rotate(0deg) scale(1)' },
          '25%': {
            transform: 'translate(30px, -30px) rotate(90deg) scale(1.1)',
          },
          '50%': {
            transform: 'translate(-20px, 20px) rotate(180deg) scale(0.9)',
          },
          '75%': {
            transform: 'translate(40px, 10px) rotate(270deg) scale(1.05)',
          },
        },
      }}
    />

    {/* Shape 2 */}
    <Box
      sx={{
        position: 'absolute',
        width: 150,
        height: 150,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
        opacity: 0.03,
        top: '60%',
        right: '15%',
        animation: 'floatAround2 20s ease-in-out infinite',
        animationDelay: '5s',
        '@keyframes floatAround2': {
          '0%, 100%': { transform: 'translate(0, 0) rotate(0deg) scale(1)' },
          '25%': {
            transform: 'translate(30px, -30px) rotate(90deg) scale(1.1)',
          },
          '50%': {
            transform: 'translate(-20px, 20px) rotate(180deg) scale(0.9)',
          },
          '75%': {
            transform: 'translate(40px, 10px) rotate(270deg) scale(1.05)',
          },
        },
      }}
    />

    {/* Shape 3 */}
    <Box
      sx={{
        position: 'absolute',
        width: 180,
        height: 180,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #10b981, #06b6d4)',
        opacity: 0.03,
        bottom: '20%',
        left: '20%',
        animation: 'floatAround3 20s ease-in-out infinite',
        animationDelay: '10s',
        '@keyframes floatAround3': {
          '0%, 100%': { transform: 'translate(0, 0) rotate(0deg) scale(1)' },
          '25%': {
            transform: 'translate(30px, -30px) rotate(90deg) scale(1.1)',
          },
          '50%': {
            transform: 'translate(-20px, 20px) rotate(180deg) scale(0.9)',
          },
          '75%': {
            transform: 'translate(40px, 10px) rotate(270deg) scale(1.05)',
          },
        },
      }}
    />
  </Box>
));

// Company logo component using the SVG
const CompanyLogo = memo(() => (
  <Box
    component="img"
    src={quibbleaiLogoDark}
    alt="Quibble AI"
    loading="eager"
    sx={{
      height: { xs: 60, sm: 80 },
      width: 'auto',
      display: 'block',
      margin: '0 auto 2rem',
      animation: 'logoFloat 3s ease-in-out infinite',
      '@keyframes logoFloat': {
        '0%, 100%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-8px)' },
      },
    }}
  />
));

// Modern circular spinner component
const CircularSpinner = memo(() => (
  <Box
    sx={{
      position: 'relative',
      width: 60,
      height: 60,
      margin: '0 auto 1.5rem',
    }}
  >
    {/* Outer spinner */}
    <Box
      sx={{
        width: 60,
        height: 60,
        border: '3px solid #f3f4f6',
        borderTop: '3px solid #D0304D',
        borderRight: '3px solid #4630D0',
        borderRadius: '50%',
        animation: 'spin 1.2s linear infinite',
        '@keyframes spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    />

    {/* Inner spinner */}
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        width: 40,
        height: 40,
        border: '2px solid transparent',
        borderTop: '2px solid #EC3C86',
        borderRadius: '50%',
        animation: 'spinReverse 0.8s linear infinite',
        '@keyframes spinReverse': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(-360deg)' },
        },
      }}
    />
  </Box>
));

// Dynamic progress bar with logo colors
const DynamicProgressBar = memo(({ progress }: { progress: number }) => (
  <Box
    sx={{
      width: { xs: 280, sm: 320 },
      height: 6,
      background: '#f3f4f6',
      borderRadius: 3,
      margin: '2rem auto 0',
      overflow: 'hidden',
      boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
    }}
  >
    <Box
      sx={{
        height: '100%',
        width: `${progress}%`,
        background:
          'linear-gradient(90deg, #D0304D 0%, #4630D0 25%, #EC3C86 75%, #BE3CEC 100%)',
        backgroundSize: '200% 100%',
        borderRadius: 3,
        animation: 'progressShine 2s linear infinite',
        boxShadow: '0 1px 4px rgba(208, 48, 77, 0.3)',
        transition: 'width 0.3s ease-out',
        '@keyframes progressShine': {
          '0%': { backgroundPosition: '-200% 0%' },
          '100%': { backgroundPosition: '200% 0%' },
        },
      }}
    />
  </Box>
));

// Status dots with logo colors
const StatusDots = memo(() => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: 1,
      mt: 1.5,
    }}
  >
    {[0, 1, 2, 3].map(index => (
      <Box
        key={index}
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: ['#D0304D', '#4630D0', '#EC3C86', '#BE3CEC'][index],
          animation: 'dotPulse 1.5s ease-in-out infinite',
          animationDelay: `${index * 0.2}s`,
          '@keyframes dotPulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 0.7 },
            '50%': { transform: 'scale(1.2)', opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
));

function AppLoader() {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);

  const messages = useMemo(
    () => [
      'Automated Food Ordering',
      'Intelligent Reservation Management',
      '24/7 General Support',
      'Preparing your workspace...',
      'Almost ready...',
      'Welcome to Quibble AI!',
    ],
    []
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        const increment = Math.random() * 12 + 3;
        const newProgress = Math.min(prev + increment, 100);

        const newMessageIndex = Math.floor(newProgress / 17);
        if (newMessageIndex !== messageIndex && messages[newMessageIndex]) {
          setMessageIndex(newMessageIndex);
        }

        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setMessageIndex(messages.length - 1);
          }, 800);
        }

        return newProgress;
      });
    }, 250);

    return () => clearInterval(interval);
  }, [messageIndex, messages]);

  return (
    <Box
      sx={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <FloatingShapes />

      <Box
        sx={{
          textAlign: 'center',
          zIndex: 10,
          position: 'relative',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          padding: { xs: '2rem', sm: '3rem' },
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {/* Quibble AI Logo */}
        <CompanyLogo />

        {/* Loading Spinner Section */}
        <Box sx={{ mt: 2 }}>
          <CircularSpinner />

          <Typography
            sx={{
              color: '#4b5563',
              fontSize: '1rem',
              fontWeight: 500,
              animation: 'loadingTextPulse 2s ease-in-out infinite',
              '@keyframes loadingTextPulse': {
                '0%, 100%': { opacity: 0.6 },
                '50%': { opacity: 1 },
              },
            }}
          >
            {messages[messageIndex]}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <DynamicProgressBar progress={progress} />

        {/* Status Dots */}
        <StatusDots />
      </Box>
    </Box>
  );
}

export default memo(AppLoader);
